import * as React from 'react';
import CircleLoader from '../CircleLoader/CircleLoader';
import cn from '~modules/cn';
import ChevronDownIcon from '../../../assets/chevron-down.svg';
import ChevronDownHighlightIcon from '../../../assets/chevron-down-highlight.svg';
import ChevronRightIcon from '../../../assets/chevron.svg';
import './relation-summary.scss';

type DivProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
export interface RelationSummaryProps {
    value: string[];
    summaryGenerator: (value: string[]) => React.ReactNode;
    open?: boolean;
    isHighlight?: boolean;
    disabled?: boolean;
    dataTip?: string;
    hideIcon?: boolean;
    loading?: boolean;
}

// This component renders a summary of a related objects
// e.g. for project with 3 collaborators it would render "3 collaborators"
//      in this case `value` is the collaborator user ids, and summaryGenerator is a function that returns "3 collaborators" from an array of 3 userIds
const RelationSummary = React.forwardRef<HTMLDivElement, RelationSummaryProps & DivProps>((props: RelationSummaryProps & DivProps, ref): JSX.Element => {
    const { value, summaryGenerator, className, open, isHighlight, disabled, dataTip, loading, hideIcon, ...rest } = props;
    const openInLink = open === undefined; // if open is undefined, then it's a link and summary is not expendable
    const summary = summaryGenerator(value);
    let icon: JSX.Element | null = openInLink ? <ChevronRightIcon /> : <ChevronDownIcon />;
    if (isHighlight && !openInLink) {
        icon = <ChevronDownHighlightIcon />;
    }
    if (loading) {
        icon = <CircleLoader type="sharp" subtype="both-semi-1" color="dark" className="relation-summary__loader" />;
    }
    if (hideIcon) {
        icon = null;
    }

    return (
        <div
            {...rest}
            ref={ref}
            data-tip={dataTip}
            className={cn('relation-summary', { open, empty: disabled || loading, link: openInLink, 'no-action': hideIcon }, className)}
        >
            <span className="relation-summary__summary-text">{summary}</span>
            {icon && <span className="relation-summary__icon">{icon}</span>}
        </div>
    );
});

RelationSummary.displayName = 'RelationSummary';

export default RelationSummary;
